/**
 * EquipmentBarcodeSticker — client-side renderer for an equipment
 * sticker (equipment name + Code128 barcode + short human-readable ID)
 * matching the layout the thermal printer emits. Used for:
 *
 *   1. Browser-print fallback when no network thermal printer is
 *      configured (a big use-case: kitchens that only have a regular
 *      A4 printer for labels).
 *   2. A visual preview of what the thermal print will look like before
 *      staff commit to sending 40+ labels to a $600 roll of docket tape.
 *
 * Renders one sticker per <EquipmentBarcodeSticker /> — the browser
 * print CSS in `printStyles()` wraps them in a page-break-friendly grid
 * so `Print all` fits ~8 per A4 page.
 */
import { useEffect, useRef } from "react";
import JsBarcode from "jsbarcode";
import { QRCodeSVG } from "qrcode.react";

/**
 * Small, self-contained Code128 barcode component using `jsbarcode`.
 * We render into a plain <svg> ref so the SVG can be printed at
 * arbitrary sizes without loss.
 */
export function BarcodeSvg({ value, height = 60, fontSize = 14 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !value) return;
    try {
      JsBarcode(ref.current, value, {
        format: "CODE128",
        height,
        fontSize,
        margin: 0,
        displayValue: true,
        font: "monospace",
        lineColor: "#000",
      });
    } catch { /* invalid barcode value — silently skip */ }
  }, [value, height, fontSize]);
  return <svg ref={ref} data-testid="barcode-svg" />;
}

/** One printable sticker card — kept identical between preview and
 *  browser-print so what you see is what prints. */
export default function EquipmentBarcodeSticker({ equipment, siteName }) {
  return (
    <div
      className="print-sticker inline-block bg-white rounded-sm p-3 text-center"
      style={{ width: "80mm" }}
      data-testid={`sticker-${equipment.id}`}
    >
      {siteName && (
        <div className="font-mono text-[10px] tracking-wider uppercase border-b border-black pb-1 mb-2">
          {siteName}
        </div>
      )}
      <div className="font-display font-extrabold text-lg leading-tight mb-1">
        {equipment.name}
      </div>
      <div className="font-mono text-[10px] uppercase text-charcoal-soft mb-2">
        {(equipment.type || "").replace("_", " ")}
        {equipment.location ? ` · ${equipment.location}` : ""}
      </div>
      <div className="flex justify-center items-center gap-3">
        <BarcodeSvg value={equipment.barcode_id || equipment.id} height={54} fontSize={13} />
        {equipment.id && (
          <div className="flex flex-col items-center shrink-0">
            <QRCodeSVG
              value={`${(typeof window !== "undefined" && window.location.origin) || ""}/e/${equipment.id}`}
              size={54}
              level="M"
              marginSize={0}
              aria-label="Scan to log temperature"
            />
            <div className="font-mono text-[8px] mt-0.5">SCAN: LOG TEMP</div>
          </div>
        )}
      </div>
      <div className="font-mono text-[10px] mt-2 text-charcoal-soft">
        Target {equipment.target_min_c}° to {equipment.target_max_c}°C
      </div>
    </div>
  );
}

/** CSS injected into the print window so the OS print dialog gets a
 *  clean, page-break-aware layout. Called once when the print window
 *  opens. */
export function printStyles() {
  return `
    * { box-sizing: border-box; }
    body {
      margin: 0;
      padding: 12mm;
      font-family: system-ui, -apple-system, sans-serif;
      background: #fff;
      color: #000;
    }
    .sheet {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 8mm;
    }
    .print-sticker {
      break-inside: avoid;
      page-break-inside: avoid;
      border: 2px solid #000;
      border-radius: 2px;
      padding: 4mm;
      text-align: center;
    }
    .heading {
      font-family: 'Courier New', monospace;
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 1px;
      border-bottom: 1px solid #000;
      padding-bottom: 2px;
      margin-bottom: 4px;
    }
    .name {
      font-size: 16px;
      font-weight: 900;
      line-height: 1.1;
      margin-bottom: 2px;
    }
    .type {
      font-family: 'Courier New', monospace;
      font-size: 10px;
      text-transform: uppercase;
      color: #666;
      margin-bottom: 4px;
    }
    .barcode-holder {
      display: flex;
      justify-content: center;
      margin: 2mm 0;
    }
    .target {
      font-family: 'Courier New', monospace;
      font-size: 10px;
      color: #666;
      margin-top: 4px;
    }
    @page { margin: 8mm; }
    @media print { .no-print { display: none; } }
  `;
}
